<template>
    <ClientOnly v-if="!props.hideDialogTriger">
        <VJoyDialogTrigger :dialog="localeSelectionDialog">
            <LocaleSelectionButton :locale="currentLanguageCode" />
        </VJoyDialogTrigger>
        <template #fallback>
            <LocaleSelectionButton loading :locale="currentLanguageCode" />
        </template>
    </ClientOnly>
    <VJoyDialog id="localeSelectionDialog" ref="localeSelectionDialog" data-testid="locale-selection-dialog">
        <template #dialog-header>{{ $t('navbar.feat.locale-selection.dialog.title') }}</template>
        <template #dialog-subheader>{{ $t('navbar.feat.locale-selection.dialog.subtitle') }}</template>
        <template #dialog-body>
            <CountryAndLanguageSelection
                :country-label="$t('navbar.feat.locale-selection.dialog.country.select.label')"
                :current-locale="currentLocale"
                :language-label="$t('navbar.feat.locale-selection.dialog.language.select.label')"
                data-testid="country-and-language-selection"
                @update:country-code="handleCountryUpdate"
                @update:language-code="handleLanguageUpdate"
            />
            <CurrencySelection
                v-if="isCurrencySelectionEnabled"
                data-testid="currency-selection"
                class="locale-selection__currency-selection"
                :selected-currency="userCurrency"
                @update:currency="handleCurrencyUpdate"
            />
        </template>
        <template #dialog-confirm>
            <VJoyButton
                :label="$t('navbar.feat.locale-selection.dialog.cta.continue')"
                size="small"
                variant="main"
                :disabled="!isCtaEnabled"
                :loading="isLoading"
                @click="handleClickOnContinue"
            />
        </template>
    </VJoyDialog>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {type CurrencyCode} from '@navbar-api';
    import {UserPreferences} from '@navbar-logged/registry';
    import {storeToRefs} from 'pinia';
    import {CountryAndLanguageSelection} from '#components';
    import {useAuth, useLocale, useTranslation, type Host, type Language} from '#imports';
    import {pushVJoySnackbar, VJoyButton, VJoyDialog, VJoyDialogTrigger} from '@maltjoy/core-vue';
    import CurrencySelection from './CurrencySelection.vue';
    import LocaleSelectionButton from './LocaleSelectionButton.vue';
    import {useLocalizationSelection} from '../composables/useLocalizationSelection';
    import {languageCodeFromLocale} from '../utils/localeSelection';

    const props = defineProps<{
        hideDialogTriger?: boolean;
    }>();

    // Stores
    const authenticationStore = useAuth();
    const {userCurrency, currentIdentityIsClient, loggedIn} = storeToRefs(authenticationStore);
    const {isMultiLocaleHost: isMaltCom} = useLocale();

    // Composables
    const {changeLanguageAndRedirect, getCurrentLocale} = useLocalizationSelection();
    const currentLocale = getCurrentLocale();
    const currentLanguageCode = languageCodeFromLocale(currentLocale).toUpperCase();
    const isCurrencySelectionEnabled = computed(() => isMaltCom.value && (!loggedIn.value || !currentIdentityIsClient.value));
    const {t} = useTranslation();

    // Refs
    const localeSelectionDialog = ref<InstanceType<typeof VJoyDialog> | null>(null);
    const selectedLanguage = ref<Language | undefined>();
    const selectedCurrency = ref<CurrencyCode | undefined>();
    // Enable CTA on malt.com because selected values are just suggested
    // Disabled on other hosts, and enable it on change
    const isCtaEnabled = ref(!!isMaltCom.value);
    const isLoading = ref(false);

    type CountryAndLanguageSelectionUpdateEvent = {
        maybeSelectedLocale: string | undefined;
        maybeSelectedHost: Host | undefined;
        maybeSelectedLanguage: Language | undefined;
    };

    // Handlers
    function handleCountryUpdate(_: string, {maybeSelectedLanguage}: CountryAndLanguageSelectionUpdateEvent) {
        selectedLanguage.value = maybeSelectedLanguage;
        isCtaEnabled.value = true;
    }

    function handleLanguageUpdate(_: string, {maybeSelectedLanguage}: CountryAndLanguageSelectionUpdateEvent) {
        selectedLanguage.value = maybeSelectedLanguage;
        isCtaEnabled.value = true;
    }

    function handleCurrencyUpdate(currencyCode: CurrencyCode) {
        selectedCurrency.value = currencyCode;
        isCtaEnabled.value = true;
    }

    async function handleClickOnContinue() {
        isLoading.value = true;

        if (selectedCurrency.value) {
            try {
                if (loggedIn.value) {
                    await UserPreferences.updatePreferredCurrency({userCurrencyUpdateRequest: {currencyCode: selectedCurrency.value}});
                }

                // will set the cookie preference
                authenticationStore.updateUserCurrency(selectedCurrency.value);

                location.reload();
            } catch (error) {
                pushVJoySnackbar({
                    props: {
                        message: t('generic.technical.error'),
                        level: 'error',
                    },
                });
            }
        }

        if (selectedLanguage.value) {
            await changeLanguageAndRedirect(selectedLanguage.value);
        }

        isLoading.value = false;
        localeSelectionDialog.value?.hide();
    }

    defineExpose({
        dialog: localeSelectionDialog,
    });
</script>
<style lang="scss" scoped>
    .locale-selection {
        &__currency-selection {
            margin-top: var(--joy-core-spacing-6);
        }
    }
</style>
